import React, { useEffect, useState } from "react";
import { Form, Table, Flex, TimePicker, message, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { v4 as uuidv4 } from "uuid";
import {
  AddItemButton,
  CustomButton,
  CustomInput,
  CustomParagraph,
  CustomTitle,
} from "../../../components";
import PhoneInput from "react-phone-input-2";
import dayjs from "dayjs";
import editIcon from "../../../assets/images/edit-gray-icon.svg";
import deleteIcon from "../../../assets/images/delete.svg";
import { isValidPhoneNumber } from "libphonenumber-js";
import { GetSettingsNewCouriers } from "../../../api's/settings";
import { useSpin } from "../../../components/SpinContext";
import { useToken } from "../../../common/helper";
import {
  GetAirportsContacts,
  AddAirportContact,
  UpdateAirportContact,
  DeleteAirportContact,
} from "../../../api's/airports";
import moment from "moment";
import { useSelector } from "react-redux";

const timeFormatRegex = /^(0[1-9]|1[0-2]):([0-5][0-9]) (AM|PM)$/;

const NewCompanyContacts = () => {
  const location = useLocation();
  const { showSpin, hideSpin } = useSpin();
  const { token, removeToken } = useToken();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const [contactsList, setContactsList] = useState([]);

  const mainMenu = useSelector((state) => state.menu.items);
  const airportAllPermissions = mainMenu?.find(
    (items) => items?.name.toLowerCase() == "airports"
  );
  const airportPermissions = airportAllPermissions?.permissions;

  // Add this useEffect to update form values when editingKey changes
  useEffect(() => {
    if (editingKey) {
      const editingIndex = contactsList.findIndex(
        (contact) => contact.id === editingKey
      );
      if (editingIndex !== -1) {
        const editingContact = contactsList[editingIndex];
        form.setFieldsValue({
          rows: {
            [editingIndex]: {
              role: editingContact.role,
              name: editingContact.name,
              start_time: editingContact.start_time
                ? dayjs(editingContact.start_time)
                : null,
              end_time: editingContact.end_time
                ? dayjs(editingContact.end_time)
                : null,
              email: editingContact.email,
              phone_number: editingContact.phone_number,
              notes: editingContact.notes,
            },
          },
        });
      }
    }
  }, [editingKey, contactsList, form]);

  // console.log('contact list : ' , contactsList)
  // console.log('editingKey list : ' , editingKey)

  const airport = location.state?.airport;
  const airline = location.state?.airline;

  // console.log('airport  and airline' , airport , airline)

  const breadcrumbItems = [
    { title: "Airports", href: "/airport-list" },
    {
      title: airport?.name || "Unknown Airport",
      href: `/airport/${airport?.code}`,
      state: { airport },
    },
    {
      title: airline?.name || "Unknown Airline",
      href: `/airline-detail/${airline.name
        .replace(/\s+/g, "-")
        .toLowerCase()}`,
      state: { airport, airline },
    },
    { title: "Company Contact" },
  ];

  const unauthorize = (status) => {
    if (status === 401) {
      removeToken();
      navigate("/login");
    }
  };

  // Fetch all contacts
  const GetAllContacts = () => {
    showSpin();
    let query = `?airport=${airport?.IATA}&airline=${airline?.name}`;
    GetAirportsContacts(query, token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            if (data?.data?.data && data?.data?.data?.length > 0) {
              setContactsList(data?.data?.data);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };
  useEffect(() => {
    GetAllContacts();
  }, []);

  // Save contact to server
  const saveContactToServer = async (contact) => {
    let model = {
      ...contact,
      airport_name: airport?.IATA,
      airline_name: airline?.name,
    };
    try {
      showSpin();
      const response = await AddAirportContact(model, token);
      if (response?.data?.data?.is_success) {
        message.success("Contact added successfully");
        GetAllContacts();
      } else {
        message.error(response?.data?.data?.message || "Failed to add contact");
      }
    } catch (error) {
      unauthorize(error);
      message.error("Failed to add contact");
    } finally {
      hideSpin();
    }
  };

  // // Update contact on server
  // const updateContactOnServer = async (contact) => {
  //   try {
  //     showSpin();
  //     const response = await UpdateAirportContact(contact, token);
  //     if (response?.data?.data?.is_success) {
  //       message.success("Contact updated successfully");
  //       GetAllContacts();
  //     } else {
  //       message.error(response?.data?.data?.message || "Failed to update contact");
  //     }
  //   } catch (error) {
  //     unauthorize(error);
  //     message.error("Failed to update contact");
  //   } finally {
  //     hideSpin();
  //   }
  // };

  // Delete contact from server
  const deleteContactFromServer = async (contactId) => {
    try {
      showSpin();
      const response = await DeleteAirportContact(contactId, token);
      if (response?.data?.data?.is_success) {
        // Update local state immediately
        setContactsList((prevContacts) =>
          prevContacts.filter((contact) => contact.id !== contactId)
        );
        message.success("Contact deleted successfully");
      } else {
        message.error(
          response?.data?.data?.message || "Failed to delete contact"
        );
      }
    } catch (error) {
      unauthorize(error);
      message.error("Failed to delete contact");
    } finally {
      hideSpin();
    }
  };

  const handleSave = (index) => {
    form
      .validateFields()
      .then((values) => {
        const parseToTimestamp = (time) => {
          if (!time) return null;
          if (typeof time === "number") return time;
          if (time instanceof dayjs) return time.valueOf();
          return dayjs(time, "h:mm A").valueOf();
        };

        const updatedContact = {
          ...contactsList[index],
          ...values.rows[index],
          start_time: parseToTimestamp(values.rows[index].start_time),
          end_time: parseToTimestamp(values.rows[index].end_time),
        };

        if (updatedContact.id) {
          // Update existing contact
          // updateContactOnServer(updatedContact);
        } else {
          // Add new contact
        }
        saveContactToServer(updatedContact);

        setEditingKey("");
      })
      .catch((error) => {
        console.log("Validation Failed:", error);
        message.error("Please fill all required fields");
      });
  };

  const handleDelete = (record) => {
    setDeleteData(record);
    setShowDeleteModal(true);
  };

  // const confirmDelete = () => {
  //   if (deleteData) {
  //     deleteContactFromServer(deleteData.id);
  //   }
  // };

  const handleCancel = (id) => {
    const contact = contactsList.find((contact) => contact.id === id);

    if (contact?.created_at) {
      setEditingKey("");
    } else {
      setContactsList((prevContacts) =>
        prevContacts.filter((contact) => contact.id !== id)
      );
      setEditingKey("");
    }
  };

  const handleInputChange = (index, field, value) => {
    setContactsList((prevContacts) => {
      const updatedContacts = [...prevContacts];
      updatedContacts[index] = { ...updatedContacts[index], [field]: value };
      return updatedContacts;
    });
  };

  const addRow = () => {
    const hasIncomplete = contactsList.some((contact) => !contact.created_at);

    if (hasIncomplete) {
      message.warning(
        "Please complete the previous record before adding a new one."
      );
      return;
    }

    const newContact = {
      id: uuidv4(),
      role: "",
      name: "",
      start_time: "",
      end_time: "",
      email: "",
      phone_number: "",
      notes: "",
    };

    setContactsList((prevContacts) => [...prevContacts, newContact]);
    setEditingKey(newContact.id);
  };

  // Helper function to convert timestamp to display format
  const formatTimeFromTimestamp = (timestamp) => {
    if (!timestamp) return "--";
    return dayjs(timestamp).format("h:mm A");
  };

  const handleEdit = (record) => {
    setEditingKey(record.id);
  };

  const columns = [
    {
      title: "Role",
      dataIndex: "role",
      className: "contacts-table-form-flex",
      width: 110,
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <Form.Item
            name={["rows", index, "role"]}
            rules={[{ required: true, message: "Please enter role" }]}
          >
            <CustomInput
              placeholder="Role"
              value={contactsList[index].role}
              onChange={(e) => handleInputChange(index, "role", e.target.value)}
              height="28px"
              width="105px"
              border="0px"
              bgColor="#F3F3F3"
              textColor="#5E5E5E"
              borderRadius="4px"
              placeholderColor="#5E5E5E"
              style={{
                fontSize: "12px",
                backgroundColor: "#f3f3f3",
                border: "0",
              }}
            />
          </Form.Item>
        ) : (
          <CustomParagraph
            noText="--"
            text={contactsList[index].role}
            fontSize={12}
            fontWeight={400}
            color="#000"
            marginBottom={0}
          />
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 105,
      className: "contacts-table-form-flex",
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <Form.Item
            name={["rows", index, "name"]}
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <CustomInput
              placeholder="Name"
              value={contactsList[index].name}
              onChange={(e) => handleInputChange(index, "name", e.target.value)}
              height="28px"
              width="100px"
              border="0px"
              bgColor="#F3F3F3"
              textColor="#5E5E5E"
              borderRadius="4px"
              placeholderColor="#5E5E5E"
              style={{
                fontSize: "12px",
                backgroundColor: "#f3f3f3",
                border: "0",
              }}
            />
          </Form.Item>
        ) : (
          <CustomParagraph
            noText="--"
            text={contactsList[index].name}
            fontSize={12}
            fontWeight={400}
            color="#000"
            marginBottom={0}
          />
        );
      },
    },
    {
      title: "Shift Hours",
      dataIndex: "shiftHours",
      width: 100,
      className: "contacts-table-form-flex",
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: "8px",
            }}
          >
            <Form.Item
              className="zl-hours-input"
              name={["rows", index, "start_time"]}
              rules={[
                {
                  required: false,
                  message: "Start time is required",
                },
              ]}
            >
              <TimePicker
                use12Hours
                format="h:mm A"
                placeholder="00:00 AM"
                value={
                  contactsList[index].start_time
                    ? dayjs(contactsList[index].start_time)
                    : null
                }
                onChange={(time) => {
                  if (time) {
                    handleInputChange(index, "start_time", time.valueOf()); // stores in ms
                  } else {
                    handleInputChange(index, "start_time", null);
                  }
                }}
                style={{
                  height: "28px",
                  width: "95px",
                  backgroundColor: "#F3F3F3",
                  color: "#5E5E5E",
                  borderRadius: "4px",
                  border: "none",
                  boxShadow: "none",
                  fontSize: "12px",
                }}
              />
            </Form.Item>
            <span>-</span>
            <Form.Item
              className="zl-hours-input"
              name={["rows", index, "end_time"]}
              rules={[
                {
                  required: false,
                  message: "End time is required",
                },
              ]}
            >
              <TimePicker
                use12Hours
                format="h:mm A"
                placeholder="00:00 PM"
                value={
                  contactsList[index].end_time
                    ? dayjs(contactsList[index].end_time)
                    : null
                }
                onChange={(time) => {
                  if (time) {
                    handleInputChange(index, "end_time", time.valueOf()); // stores in ms
                  } else {
                    handleInputChange(index, "end_time", null);
                  }
                }}
                style={{
                  height: "28px",
                  width: "95px",
                  backgroundColor: "#F3F3F3",
                  color: "#5E5E5E",
                  borderRadius: "4px",
                  border: "none",
                  boxShadow: "none",
                  fontSize: "12px",
                }}
              />
            </Form.Item>
          </div>
        ) : (
          <CustomParagraph
            text={`${formatTimeFromTimestamp(
              contactsList[index].start_time
            )} - ${formatTimeFromTimestamp(contactsList[index].end_time)}`}
            fontSize={14}
            fontWeight={400}
            color="#000"
          />
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 130,
      className: "contacts-table-form-flex",
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <Form.Item
            name={["rows", index, "email"]}
            rules={[{ required: true, message: "Please enter email" },
                                { type: "email", message: "Please enter a valid email" },
              
            ]}
          >
            <CustomInput
              placeholder="Email"
              value={contactsList[index].email}
              onChange={(e) =>
                handleInputChange(index, "email", e.target.value)
              }
              height="28px"
              width="170px"
              border="0px"
              bgColor="#F3F3F3"
              textColor="#5E5E5E"
              borderRadius="4px"
              placeholderColor="#5E5E5E"
              style={{
                fontSize: "12px",
                backgroundColor: "#f3f3f3",
                border: "0",
              }}
            />
          </Form.Item>
        ) : (
          <CustomParagraph
            noText="--"
            text={contactsList[index].email}
            fontSize={12}
            fontWeight={400}
            color="#000"
            marginBottom={0}
          />
        );
      },
    },
    {
      title: "Phone number",
      dataIndex: "phone_number",
      className: "airports-contacts-form-phone contacts-table-form-flex",

      width: 150,
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <Form.Item
            name={["rows", index, "phone_number"]}
            // rules={[
            //   {
            //     validator: (_, value) => {
            //       if (!value) {
            //         return Promise.reject("Please enter phone number");
            //       }

            //       const digitsOnly = value.replace(/\D/g, "");
            //       if (digitsOnly.length >= 10) {
            //         return Promise.resolve();
            //       }
            //       return Promise.reject("Please enter a valid phone number");
            //     },
            //   },
            // ]}

            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.trim() === "") {
                    return Promise.reject(
                      new Error("Please enter phone number")
                    ); // Reject if empty
                  }
                  try {
                    // Use libphonenumber to validate
                    if (isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(new Error("Invalid phone number"));
                    }
                  } catch (error) {
                    return Promise.reject(new Error("Invalid phone number"));
                  }
                },
              },
            ]}
          >
            <PhoneInput
              className={"airports-contacts-form-phone"}
              country={"us"}
              value={contactsList[index].phone}
              style={{ fontSize: "12px", width: "180px" }}
              onChange={(value, countryData, e, formattedValue) =>
                handleInputChange(index, "phone_number", formattedValue)
              }
            />
          </Form.Item>
        ) : (
          <CustomParagraph
            noText="--"
            text={contactsList[index].phone_number}
            fontSize={12}
            fontWeight={400}
            color="#000"
            marginBottom={0}
          />
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      width: 230,
      className: "contacts-table-form-flex",
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <Form.Item
            name={["rows", index, "notes"]}
            rules={[{ required: true, message: "Please enter notes" }]}
          >
            <CustomInput
              placeholder="Notes"
              value={contactsList[index].notes}
              onChange={(e) =>
                handleInputChange(index, "notes", e.target.value)
              }
              height="28px"
              width="230px"
              border="0px"
              bgColor="#F3F3F3"
              textColor="#5E5E5E"
              borderRadius="4px"
              placeholderColor="#5E5E5E"
              style={{
                fontSize: "12px",
                backgroundColor: "#f3f3f3",
                border: "0",
              }}
            />
          </Form.Item>
        ) : (
          <CustomParagraph
            noText="--"
            text={contactsList[index].notes}
            fontSize={12}
            fontWeight={400}
            color="#000"
            marginBottom={0}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      className: "contacts-table-form-flex",
      render: (_, record, index) => {
        return editingKey === record?.id ? (
          <div
            style={{ display: "flex", placeContent: "flex-start", gap: "8px" }}
          >
            <CustomButton
              text="Save"
              onClick={() => handleSave(index)}
              width={52}
              height={28}
              bgColor="#000"
              color="#fff"
              fontSize={12}
              fontWeight={500}
              borderRadius={8}
            />
            <CustomButton
              text="Cancel"
              onClick={() => handleCancel(record?.id)}
              width={52}
              height={28}
              bgColor="transparent"
              color="#000"
              fontSize={12}
              fontWeight={500}
              boxShadow="none"
            />
          </div>
        ) : (
          <Flex gap={6}>
            {airportPermissions?.is_update && (
              <Flex
                justify="center"
                align="center"
                style={{ width: "32px", height: "32px", cursor: "pointer" }}
              >
                <img
                  src={editIcon}
                  alt=""
                  onClick={() => handleEdit(contactsList?.[index])}
                />
              </Flex>
            )}
            {airportPermissions?.is_delete && (
              <Flex
                justify="center"
                align="center"
                style={{ width: "32px", height: "32px", cursor: "pointer" }}
              >
                <img
                  src={deleteIcon}
                  alt=""
                  onClick={() => deleteContactFromServer(record.id)}
                />
              </Flex>
            )}
          </Flex>
        );
      },
    },
  ];

  return (
    <section className="airline-list">
      <Flex vertical gap={16}>
        <Breadcrumbs items={breadcrumbItems} />
        <CustomTitle text="Company Contacts" fontSize="24px" fontWeight="700" />
        <Form
          form={form}
          layout="vertical"
          initialValues={{ rows: contactsList }}
        >
          <div className="settings-content-wrapper">
            <Table
              // className="zl-outline-table"
              key={contactsList.length}
              columns={columns}
              dataSource={contactsList}
              pagination={false}
              className="airline-contacts-table"
              rowHoverable={false}
              rowKey="key"
            />
          </div>
        </Form>

        {airportPermissions?.is_create && (
          <AddItemButton
            onClick={addRow}
            text={"Create another"}
            marginTop="4px"
            fontSize="12px"
            fontWeight="600"
            height="28px"
            width="28px"
          />
        )}
      </Flex>

      {/* <Modal
        title="Delete Contact"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteData(null);
        }}
        okText="Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this contact?</p>
      </Modal> */}
    </section>
  );
};

export default NewCompanyContacts;
